/**
 * Hook to fetch brand collections with products
 * Used for dynamic navigation that only shows brands with available products
 */

import { useState, useEffect } from 'react';
import { getCollectionsWithProductCount, type CollectionWithProductCount } from '~/lib/shopify/api';

// Known brand handles in the store
const BRAND_HANDLES = ['jabra', 'poly', 'axtel', 'plantronics', 'sennheiser', 'logitech', 'yealink'];

// Badge configuration for specific brands
const BRAND_BADGES: Record<string, string> = {
  jabra: 'Popular',
  axtel: 'Value',
};

export interface BrandLink {
  label: string;
  href: string;
  badge?: string;
  isViewAll?: boolean;
}

interface UseBrandCollectionsResult {
  brands: BrandLink[];
  isLoading: boolean;
  error: Error | null;
}

export function useBrandCollections(): UseBrandCollectionsResult {
  const [brands, setBrands] = useState<BrandLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    async function fetchBrands() {
      try {
        const collections = await getCollectionsWithProductCount(100);

        if (!mounted) return;

        // Filter to only brand collections that have products
        const brandCollections = collections.filter(
          (collection) =>
            BRAND_HANDLES.includes(collection.handle.toLowerCase()) &&
            collection.hasProducts
        );

        // Map to brand links with proper formatting
        const brandLinks: BrandLink[] = brandCollections.map((collection) => ({
          label: collection.title,
          href: `/collections/${collection.handle}`,
          badge: BRAND_BADGES[collection.handle.toLowerCase()],
        }));

        // Sort: brands with badges first, then alphabetically
        brandLinks.sort((a, b) => {
          if (a.badge && !b.badge) return -1;
          if (!a.badge && b.badge) return 1;
          return a.label.localeCompare(b.label);
        });

        // Add "View All Brands" link at the end
        brandLinks.push({
          label: 'View All Brands',
          href: '/products',
          isViewAll: true,
        });

        setBrands(brandLinks);
      } catch (err) {
        if (!mounted) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch brands'));
        // Fallback to default brands on error
        setBrands([
          { label: 'Jabra', href: '/collections/jabra', badge: 'Popular' },
          { label: 'Poly', href: '/collections/poly' },
          { label: 'Axtel', href: '/collections/axtel', badge: 'Value' },
          { label: 'View All Brands', href: '/products', isViewAll: true },
        ]);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    fetchBrands();

    return () => {
      mounted = false;
    };
  }, []);

  return { brands, isLoading, error };
}
